/**
 * CircuitBranchGraph — board-wide bird's-eye SVG showing how every
 * Circuit on the board relates to every other via `parent_circuit_id`.
 *
 * Lives at the top of CircuitsTab so users can see "the shape of the
 * design space" before drilling into cards. Complements the per-card
 * lineage chain (which shows ONE chain) with the whole forest.
 *
 * Layout — subtree-width DFS:
 *   * Roots are circuits with no parent OR whose parent isn't in the
 *     visible set (filtered out by status).
 *   * Each leaf consumes one column; internal nodes center themselves
 *     above their descendants.
 *   * Depth = row, no overlap between sibling subtrees.
 *
 * Node colour mirrors CircuitKindBadge — main filled primary,
 * candidate dashed editorial, fixture grey, scratch soft editorial.
 * Status border: released → solid success ring, archived → faded.
 *
 * Click → scrolls the corresponding CircuitCard into view via the
 * `#circuit-{id}` anchor the tab now adds to each card.
 */
import type { CircuitDTO } from "@models/circuit-ir";
import { Box, Tooltip, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha, useTheme } from "@mui/material/styles";
import { useMemo } from "react";

interface CircuitBranchGraphProps {
  circuits: readonly CircuitDTO[];
}

const NODE_RADIUS = 7;
const COL_WIDTH = 90;
const ROW_HEIGHT = 56;
const PADDING_X = 24;
const PADDING_Y = 18;
const LABEL_OFFSET = 16;

interface LaidOutNode {
  circuit: CircuitDTO;
  x: number;
  y: number;
}

interface Layout {
  nodes: LaidOutNode[];
  edges: { from: LaidOutNode; to: LaidOutNode }[];
  width: number;
  height: number;
}

function layoutForest(circuits: readonly CircuitDTO[]): Layout {
  const byId = new Map(circuits.map((c) => [c.id, c]));
  const children = new Map<string, CircuitDTO[]>();
  for (const c of circuits) children.set(c.id, []);
  for (const c of circuits) {
    if (!c.parent_circuit_id) continue;
    const siblings = children.get(c.parent_circuit_id);
    if (siblings) siblings.push(c);
  }
  // Stable sibling order: by created_at ascending so the same project
  // always renders the same tree (and "older first" reads naturally).
  for (const list of children.values()) {
    list.sort((a, b) => a.created_at.localeCompare(b.created_at));
  }

  const roots = circuits
    .filter((c) => !c.parent_circuit_id || !byId.has(c.parent_circuit_id))
    .sort((a, b) => a.created_at.localeCompare(b.created_at));

  const nodes: LaidOutNode[] = [];
  let nextCol = 0;
  let maxDepth = 0;

  function place(c: CircuitDTO, depth: number): number {
    maxDepth = Math.max(maxDepth, depth);
    const kids = children.get(c.id) ?? [];
    let myX: number;
    if (kids.length === 0) {
      myX = nextCol;
      nextCol += 1;
    } else {
      const firstX = place(kids[0], depth + 1);
      let lastX = firstX;
      for (let i = 1; i < kids.length; i++) {
        lastX = place(kids[i], depth + 1);
      }
      myX = (firstX + lastX) / 2;
    }
    nodes.push({
      circuit: c,
      x: PADDING_X + myX * COL_WIDTH,
      y: PADDING_Y + depth * ROW_HEIGHT,
    });
    return myX;
  }

  for (const r of roots) {
    place(r, 0);
    // Leave a half-column gap between root subtrees so they don't kiss.
    nextCol += 0.5;
  }

  const nodeById = new Map(nodes.map((n) => [n.circuit.id, n]));
  const edges: { from: LaidOutNode; to: LaidOutNode }[] = [];
  for (const n of nodes) {
    const parentId = n.circuit.parent_circuit_id;
    if (!parentId) continue;
    const parent = nodeById.get(parentId);
    if (!parent) continue;
    edges.push({ from: parent, to: n });
  }

  const width = Math.max(
    PADDING_X * 2 + Math.max(0, nextCol - 0.5) * COL_WIDTH,
    160,
  );
  const height = PADDING_Y * 2 + maxDepth * ROW_HEIGHT + LABEL_OFFSET + 4;
  return { nodes, edges, width, height };
}

/** Click target: scrolls the matching `<CircuitCard>` anchor into
 *  view. CircuitsTab wraps each card in a `<Box id="circuit-{id}">`. */
function scrollToCircuit(circuitId: string) {
  const el = document.getElementById(`circuit-${circuitId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // Brief focus halo so the user sees where they landed.
  el.classList.add("circuit-card-flash");
  window.setTimeout(() => el.classList.remove("circuit-card-flash"), 1500);
}

export function CircuitBranchGraph({ circuits }: CircuitBranchGraphProps) {
  const theme = useTheme();
  const layout = useMemo(() => layoutForest(circuits), [circuits]);

  if (layout.nodes.length === 0) return null;

  return (
    <Box
      sx={(t) => ({
        position: "relative",
        border: `1px solid ${t.palette.border.default}`,
        borderRadius: 2,
        bgcolor: t.palette.surface.raised,
        px: 1.5,
        py: 1,
        mb: 2,
        overflowX: "auto",
      })}
    >
      <Typography
        variant="overline"
        sx={(t) => ({
          color: t.palette.editorial.main,
          fontSize: 10,
          letterSpacing: "0.16em",
          display: "block",
          mb: 0.5,
        })}
      >
        Branch graph · {circuits.length}
      </Typography>
      <Box
        component="svg"
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        sx={{
          width: layout.width,
          height: layout.height,
          maxWidth: "100%",
          display: "block",
        }}
      >
        {layout.edges.map((e) => (
          <line
            key={`${e.from.circuit.id}->${e.to.circuit.id}`}
            x1={e.from.x}
            y1={e.from.y + NODE_RADIUS}
            x2={e.to.x}
            y2={e.to.y - NODE_RADIUS}
            stroke={alpha(theme.palette.editorial.main, 0.45)}
            strokeWidth={1}
            strokeDasharray={e.to.circuit.kind === "candidate" ? "3 3" : "none"}
          />
        ))}
        {layout.nodes.map((n) => (
          <GraphNode key={n.circuit.id} node={n} theme={theme} />
        ))}
      </Box>
    </Box>
  );
}

interface GraphNodeProps {
  node: LaidOutNode;
  theme: Theme;
}

function GraphNode({ node, theme }: GraphNodeProps) {
  const { circuit, x, y } = node;

  // Fill by kind, stroke by status. Matches the chip palette in
  // CircuitKindBadge so the bird's-eye view reads like the cards below.
  const fill =
    circuit.kind === "main"
      ? theme.palette.primary.main
      : circuit.kind === "candidate"
        ? alpha(theme.palette.editorial.main, 0.9)
        : circuit.kind === "fixture"
          ? alpha(theme.palette.text.disabled, 0.85)
          : alpha(theme.palette.editorial.main, 0.55); // scratch

  const stroke =
    circuit.status === "released"
      ? theme.palette.success.main
      : circuit.status === "archived"
        ? alpha(theme.palette.text.disabled, 0.4)
        : circuit.status === "draft" || circuit.status === "ephemeral"
          ? alpha(theme.palette.warning.main, 0.7)
          : theme.palette.background.paper;

  const opacity = circuit.status === "archived" ? 0.45 : 1;

  const versionLabel =
    circuit.current_version_semver?.trim() ||
    (circuit.current_version_number != null
      ? `v${circuit.current_version_number}`
      : null);

  const tooltipTitle = (
    <Box
      sx={(t) => ({ fontFamily: t.fonts.mono, fontSize: 11, lineHeight: 1.5 })}
    >
      <Box sx={{ fontWeight: 700 }}>{circuit.name}</Box>
      <Box sx={{ opacity: 0.8 }}>
        {circuit.kind} · {circuit.status}
        {versionLabel ? ` · ${versionLabel}` : ""}
      </Box>
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      {/* biome-ignore lint/a11y/useSemanticElements: SVG <g> cannot be a real
          <button> element — role="button" is the standard a11y pattern for
          clickable SVG groups. */}
      <g
        role="button"
        tabIndex={0}
        aria-label={`Scroll to ${circuit.name}`}
        style={{ cursor: "pointer", opacity, outline: "none" }}
        onClick={() => scrollToCircuit(circuit.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            scrollToCircuit(circuit.id);
          }
        }}
      >
        <circle
          cx={x}
          cy={y}
          r={NODE_RADIUS}
          fill={fill}
          stroke={stroke}
          strokeWidth={2}
          strokeDasharray={circuit.kind === "candidate" ? "2 2" : "none"}
        />
        <text
          x={x}
          y={y + NODE_RADIUS + LABEL_OFFSET - 4}
          textAnchor="middle"
          fontSize={10}
          fontFamily="JetBrains Mono, ui-monospace, monospace"
          fill={theme.palette.text.secondary}
        >
          {truncate(circuit.name, 12)}
        </text>
      </g>
    </Tooltip>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}
